import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Mailcheck from "mailcheck";
import { call as rpc } from "../../../common/jsonRpcRoutes";

const EMAIL_PATTERN = /^[^\s@]+@([^\s@.]+\.)+[^\s@.]+$/;

export interface LoggedInUser {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  dob?: string | null;
  /**
   * DMN reward points balance, returned on sign-in. Undefined when the guest
   * signed in through a route that does not expose it.
   */
  points_balance?: number;
  custom_details?:
    | {
        owner_id?: string;
        marketing_preferences?: Array<{
          id: string;
          opt_in_date?: unknown;
          opt_out_date?: unknown;
        }>;
      }
    | Array<{
        owner_id?: string;
        marketing_preferences?: Array<{
          id: string;
          opt_in_date?: unknown;
          opt_out_date?: unknown;
        }>;
      }>
    | null;
}

interface AccountDetectionCallbacks {
  getEmail: () => string;
  getPassword: () => string;
  getFirstName: () => string;
  getLastName: () => string;
  onLoginSuccess: (user: LoggedInUser) => void;
  onLogout: () => void;
  onEmailPrefill: (email: string) => void;
}

/**
 * Encapsulates the login / account-detection state machine so the parent
 * component does not accumulate cognitive complexity.
 */
export function useAccountDetection(callbacks: AccountDetectionCallbacks, initialUser?: LoggedInUser | null) {
  const { t } = useTranslation();

  const [emailExists, setEmailExists] = useState(false);
  const [guestCheckout, setGuestCheckout] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser | null>(initialUser ?? null);
  const [signUpLoading, setSignUpLoading] = useState(false);
  const [signUpError, setSignUpError] = useState<string | null>(null);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const [suggestedEmail, setSuggestedEmail] = useState<string | null>(null);
  const emailBlurVersion = useRef(0);

  const handleEmailBlur = useCallback(async () => {
    const email = callbacks.getEmail();
    if (!email || !EMAIL_PATTERN.test(email)) return;

    setSuggestedEmail(null);
    Mailcheck.run({
      email,
      suggested: (suggestion) => {
        if (suggestion) setSuggestedEmail(suggestion.full);
      },
      empty: () => {},
    });

    const version = ++emailBlurVersion.current;
    try {
      const result = await rpc("/checkEmailAccount", { email });
      if (version !== emailBlurVersion.current) return;
      setEmailExists(result.exists);
      setGuestCheckout(false);
      setLoginError(null);
      setSignUpError(null);
      setSignUpSuccess(false);
    } catch (_err: unknown) {
      if (version !== emailBlurVersion.current) return;
      setEmailExists(false);
    }
  }, [callbacks]);

  const handleLogin = useCallback(async () => {
    const email = callbacks.getEmail();
    const password = callbacks.getPassword();
    if (!password) return;
    setLoginLoading(true);
    setLoginError(null);
    try {
      const result = await rpc("/loginWithEmail", {
        email,
        password,
      });
      if (result.success && result.user) {
        const user: LoggedInUser = {
          first_name: result.user.first_name,
          last_name: result.user.last_name,
          email: result.user.email,
          phone: result.user.phone ?? undefined,
          dob: result.user.dob ?? undefined,
          points_balance: result.user.points_balance ?? undefined,
          custom_details: result.user.custom_details ?? undefined,
        };
        setLoggedInUser(user);
        setEmailExists(false);
        setSuggestedEmail(null);
        callbacks.onLoginSuccess(user);
      } else {
        setLoginError(result.error ?? t("login_error", "Invalid email or password"));
      }
    } catch (_err: unknown) {
      setLoginError(t("login_failed", "Login failed. Please try again."));
    } finally {
      setLoginLoading(false);
    }
  }, [callbacks, t]);

  const handleGuestContinue = useCallback(() => {
    setGuestCheckout(true);
  }, []);

  const handleSignUp = useCallback(
    async (newsletterSignup?: boolean) => {
      const email = callbacks.getEmail();
      const password = callbacks.getPassword();
      const firstName = callbacks.getFirstName();
      const lastName = callbacks.getLastName();
      if (!email || !password || !firstName || !lastName) return;
      setSignUpLoading(true);
      setSignUpError(null);
      try {
        const result = await rpc("/signUpUser", {
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          source: "Booking",
          newsletter_signup: newsletterSignup ?? false,
        });
        if (result.success && result.authenticated && result.user) {
          const user: LoggedInUser = {
            first_name: result.user.first_name,
            last_name: result.user.last_name,
            email: result.user.email,
            phone: result.user.phone ?? undefined,
            dob: result.user.dob ?? undefined,
            points_balance: result.user.points_balance ?? undefined,
            custom_details: result.user.custom_details ?? undefined,
          };
          setLoggedInUser(user);
          setSuggestedEmail(null);
          callbacks.onLoginSuccess(user);
          setSignUpSuccess(true);
        } else if (result.success) {
          setEmailExists(true);
          setSignUpError(
            t("account_created_sign_in_required", "Your account was created. Please sign in to continue."),
          );
        } else {
          if (result.error_code === "email_exists") {
            setEmailExists(true);
          }
          setSignUpError(result.error ?? t("sign_up_error", "Sign up failed. Please try again."));
        }
      } catch (_err: unknown) {
        setSignUpError(t("sign_up_error", "Sign up failed. Please try again."));
      } finally {
        setSignUpLoading(false);
      }
    },
    [callbacks, t],
  );

  const clearSuggestedEmail = useCallback(() => setSuggestedEmail(null), []);

  const handleSocialLoginSuccess = useCallback(
    (user: LoggedInUser) => {
      setLoggedInUser(user);
      setEmailExists(false);
      setSuggestedEmail(null);
      setLoginError(null);
      callbacks.onLoginSuccess(user);
    },
    [callbacks],
  );

  /**
   * Social sign-in for a guest with no DMN account: no profile to log in with,
   * so keep the guest in the form with their social email pre-filled.
   */
  const handleSocialProfileIncomplete = useCallback(
    (email: string) => {
      setLoginError(null);
      setSuggestedEmail(null);
      callbacks.onEmailPrefill(email);
    },
    [callbacks],
  );

  const handleLogout = useCallback(async () => {
    // Revoke the OAuth token and clear the server-side session before
    // touching local state, so a stale identity is never left behind
    // (e.g. someone else's details) if it fails partway through.
    try {
      await rpc("/logout", {});
    } catch (_err: unknown) {
      setLoginError(t("logout_error", "Unable to sign out. Please try again."));
      return;
    }

    setLoggedInUser(null);
    setEmailExists(false);
    setGuestCheckout(false);
    setLoginError(null);
    setSignUpLoading(false);
    setSignUpError(null);
    setSignUpSuccess(false);
    setSuggestedEmail(null);
    callbacks.onLogout();
  }, [callbacks, t]);

  return {
    emailExists,
    guestCheckout,
    loginError,
    loginLoading,
    loggedInUser,
    signUpLoading,
    signUpError,
    signUpSuccess,
    suggestedEmail,
    handleEmailBlur,
    handleLogin,
    handleGuestContinue,
    handleLogout,
    handleSignUp,
    clearSuggestedEmail,
    handleSocialLoginSuccess,
    handleSocialProfileIncomplete,
  };
}
